/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsColor} from '../../../theme/theme';
import AccordionWrapper from '../../accordion/Accordion';
import {AccordionSectionHeader} from '../../accordion/current/AccordionSectionHeader';
import {getAccordionTopBorderStyle} from './assetDetailsAccordionStyles';
import {SpreaderGranularInfo, SpreaderGranularInfoProps} from './SpreaderGranularInfo';
import {SpreaderInfoCard, SpreaderInfoCardProps} from './SpreaderInfoCard';
import {SpreaderLiquidTotals, SpreaderLiquidTotalsProps} from './SpreaderLiquidTotals';
import {SpreaderStatTileData, SpreaderStatTileRow} from './SpreaderStatTileRow';
import {SpreaderStatusBadge} from './SpreaderStatusBadge';

export interface SpreaderAccordionProps {
  title: string;
  receivedTime?: string;
  status?: string;
  // the status column's label - `Spreader Status` by default ...
  statusLabel?: string;
  tiles?: SpreaderStatTileData[];
  // sizes the stat tiles to their content instead of evenly ...
  tilesFitContent?: boolean;
  granular?: SpreaderGranularInfoProps;
  liquidTotals?: SpreaderLiquidTotalsProps;
  // the per-manufacturer detail cards (Conditions, Liquid, Totals...) shown
  // after the Type 1 Granular Info / Liquid Totals blocks ...
  cards?: SpreaderInfoCardProps[];
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

const useStyles = makeStyles(() => ({
  // every Asset Details Drawer accordion gets this 2px teal top border to
  // separate it from the card above it ...
  header  : getAccordionTopBorderStyle(),
  details : {
    boxSizing     : 'border-box',
    display       : 'flex',
    width         : '100%',
    flexDirection : 'column',
    gap           : '16px',
    padding       : '16px 16px 24px'
  },
  summaryRow : {
    display        : 'flex',
    width          : '100%',
    alignItems     : 'flex-start',
    justifyContent : 'space-between',
    gap            : '16px'
  },
  summaryColumn : {
    display       : 'flex',
    flexDirection : 'column',
    gap           : '8px'
  },
  summaryColumnRight : {
    alignItems : 'flex-end'
  },
  summaryLabel : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey3')
  },
  summaryValue : {
    fontFamily : 'Inter',
    fontWeight : 600,
    fontSize   : '13px',
    lineHeight : '16px',
    color      : dlsColor('grey2')
  }
}));

/**
 * SpreaderAccordion renders the current theme's Spreader Type 1 accordion
 * for the Asset Details Drawer (ION-7465) - the shared `AccordionWrapper`
 * shell (`@ion/dls/src/components/accordion/Accordion`) with an
 * `AccordionSectionHeader` title, a Received Time / Status summary row, the
 * top stat tile row (`SpreaderStatTileRow`), the Granular Info block
 * (`SpreaderGranularInfo`), and the Liquid Totals block
 * (`SpreaderLiquidTotals`) for Spreader Type 1 - and, for the other
 * manufacturer layouts (Type 2 Force America / Dickey-john, Type 3 Cirus),
 * their own `cards` (`SpreaderInfoCard`), status label and content-sized
 * tiles.
 *
 * This is a purely presentational component: it renders whatever it's
 * given and doesn't know about manufacturer-specific sensor measures
 * (Cirus/ForceAmerica/DickeyJohn) - mapping those onto `tiles`/`granular`/
 * `liquidTotals` is a later integration pass, the same way
 * `AssetSummaryCardStatus` leaves state-color/label resolution to its
 * caller. Each section (summary row, stat tiles, Granular Info, Liquid
 * Totals) independently omits itself when it has nothing to show, so the
 * accordion's height always tracks whatever content is actually present.
 *
 * Expansion is uncontrolled via `defaultExpanded` (expanded by default);
 * passing `expanded` and `onExpand` instead hands control to the caller,
 * which the Asset Details Drawer uses to drive its header's
 * expand/collapse-all control. Both are forwarded straight to
 * `AccordionWrapper`.
 *
 * ```typescript
 *
 *   import {SpreaderAccordion} from
 *     '@ion/dls/src/components/assetdetails/current/SpreaderAccordion';
 *   ...
 *
 *   <SpreaderAccordion title='Spreader'
 *     receivedTime='12/16/2025 • 11:08 AM'
 *     status='In Standby'
 *     tiles={[
 *       {label: 'Granular Set Rate', value: 'N/A'},
 *       {label: 'Spread Rate Index', value: '0'},
 *       {label: 'Spinner Rate', value: '0'}
 *     ]}
 *     granular={{
 *       materialName : 'Salty',
 *       mode         : 'Closed Loop',
 *       total        : {value: '2,033,344', unit: 'mi'}
 *     }}
 *     liquidTotals={{
 *       prewetTotal : {value: '780', unit: 'gal'},
 *       directTotal : {value: '0', unit: 'gal'},
 *       dustTotal   : {value: 'N/A'}
 *     }} />
 *
 * ```
 *
 * @param   title           The accordion section header's title.
 * @param   receivedTime    The Received Time column's value, omitted when
 *                          `undefined`.
 * @param   status          The status badge's label, omitted when `undefined`.
 * @param   statusLabel     The status column's label, `Spreader Status` by
 *                          default.
 * @param   tiles           The top stat tile row's tiles.
 * @param   tilesFitContent Sizes the stat tiles to their content when `true`,
 *                          otherwise evenly.
 * @param   granular        The Granular Info block's props.
 * @param   liquidTotals    The Liquid Totals block's props.
 * @param   cards           The per-manufacturer detail cards shown after the
 *                          Granular Info and Liquid Totals blocks.
 * @param   defaultExpanded Expands the accordion initially when `true`.
 * @param   expanded        Controls the expansion state when provided.
 * @param   onExpand        Invoked with the new expansion state.
 * @returns                 The Spreader accordion.
 */
export const SpreaderAccordion = ({
  title,
  receivedTime,
  status,
  statusLabel = 'Spreader Status',
  tiles,
  tilesFitContent = false,
  granular,
  liquidTotals,
  cards = [],
  defaultExpanded = true,
  expanded,
  onExpand
}: SpreaderAccordionProps) => {
  const classes = useStyles();
  const header  = <AccordionSectionHeader title={title} />;
  const details = (
    <div className={classes.details}>
      {(receivedTime !== undefined || status !== undefined) && (
        <div className={classes.summaryRow}>
          {receivedTime !== undefined && (
            <div className={classes.summaryColumn}>
              <span className={classes.summaryLabel}>
                Spreader Received Time
              </span>
              <span className={classes.summaryValue}>
                {receivedTime}
              </span>
            </div>
          )}
          {status !== undefined && (
            <div className={classnames(classes.summaryColumn, classes.summaryColumnRight)}>
              <span className={classes.summaryLabel}>
                {statusLabel}
              </span>
              <SpreaderStatusBadge label={status} />
            </div>
          )}
        </div>
      )}
      <SpreaderStatTileRow tiles={tiles} fitContent={tilesFitContent} />
      <SpreaderGranularInfo {...granular} />
      <SpreaderLiquidTotals {...liquidTotals} />
      {cards.map(function(card, index) {
        return (
          <SpreaderInfoCard key={card.title ?? index} {...card} />
        );
      })}
    </div>
  );

  return (
    <AccordionWrapper isCustomStyle
      className={classes.header}
      header={header}
      details={details}
      padding="0px"
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onExpand={onExpand} />
  );
};

export default memo(SpreaderAccordion);
